import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { BG_COLORS, BORDER_COLORS } from '../../utils/colors';

interface GlassContainerProps {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  /** Фиксирует высоту подложки на уровне сайдбара (для страниц с пагинацией) */
  matchSidebarHeight?: boolean;
  /** Для публичных страниц без сайдбара - убирает отрицательные отступы */
  publicPage?: boolean;
  /** Максимальная ширина контейнера (для publicPage) */
  maxWidth?: string | number;
}

/**
 * Glass morphism контейнер для страниц личного кабинета
 * Автоматически выравнивается с sidebar'ом и применяет тот же стиль подложки что и сайдбар
 * Использует тот же цвет фона, border-radius, box-shadow и hover эффекты
 */
const GlassContainer: React.FC<GlassContainerProps> = ({ children, sx, matchSidebarHeight = false, publicPage = false, maxWidth }) => {
  // Для публичных страниц без сайдбара - простой контейнер без отрицательных отступов
  if (publicPage) {
    return (
      <Box
        sx={{
          background: BG_COLORS.CARD,
          backdropFilter: 'blur(10px)',
          borderRadius: '40px',
          border: `1px solid ${BORDER_COLORS.DEFAULT}`,
          boxShadow: '-10px -20px 20px 0px rgba(123, 186, 201, 0.1), 15px 30px 30px 0px rgba(123, 186, 201, 0.08)',
          p: 4,
          maxWidth: maxWidth || '900px',
          mx: 'auto',
          color: 'white',
          // GPU rendering fixes for backdrop-filter artifacts
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          ...sx as object,
        }}
      >
        {children}
      </Box>
    );
  }

  // Для страниц с сайдбаром - с отрицательными отступами для выравнивания
  return (
    <Box
      sx={{
        // Убираем ВСЕ отступы MainContent для правильного выравнивания с sidebar
        m: -3, // Убираем padding MainContent
        mt: {
          xs: 'calc(-80px - 24px)', // Убираем header space + padding (мобайл)
          sm: 'calc(-112px - 24px)' // Убираем header space + padding (десктоп)
        },
      }}
    >
      {/* Подложка в стиле sidebar - тот же фон, border-radius и эффекты */}
      <Box
        sx={{
          background: BG_COLORS.CARD,
          backdropFilter: 'blur(10px)',
          borderRadius: '40px',
          border: `1px solid ${BORDER_COLORS.DEFAULT}`,
          boxShadow: '-10px -20px 20px 0px rgba(123, 186, 201, 0.1), 15px 30px 30px 0px rgba(123, 186, 201, 0.08)',
          p: 3,
          mx: 3, // Горизонтальные отступы
          mb: 0, // Без нижнего отступа чтобы точно совпадать с sidebar
          mt: {
            xs: '84px', // ТОЧНО как в UniversalSidebar (80px + 4px)
            sm: '116px' // ТОЧНО как в UniversalSidebar (112px + 4px)
          },
          // Ограничиваем максимальную высоту чтобы не выходить за сайдбар
          boxSizing: 'border-box',
          // Фиксация высоты на уровне сайдбара (для страниц с пагинацией)
          ...(matchSidebarHeight && {
            height: {
              xs: 'calc(100dvh - 158px)',
              sm: 'calc(100dvh - 200px)'
            },
          }),
          // Ограничиваем максимальную высоту чтобы не выходить за сайдбар.
          // Раскладка снизу вверх (замерено в браузере на странице, упирающейся в потолок):
          //   xs: отступ сверху 84 + футер 60 + зазор до футера ~14 = 158
          //   sm: отступ сверху 116 + футер 66 + зазор до футера ~18 = 200
          // Было 170/244 — резерв закладывался под футер в две строки; после объединения
          // футеров он всегда одна строка (60/66), и лишние 12/44px просто пустовали.
          // ВАЖНО: те же числа продублированы в UniversalSidebar — подложка и сайдбар
          // должны заканчиваться на одной линии, менять их только вместе.
          // Используем dvh (dynamic viewport height) для корректной работы на iOS Safari
          maxHeight: {
            xs: 'calc(100dvh - 158px)',
            sm: 'calc(100dvh - 200px)'
          },
          overflow: 'hidden', // Scroll handled by SimpleBar in child components
          // Flex контейнер чтобы дочерние элементы могли использовать height: 100%
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          // GPU rendering fixes for backdrop-filter artifacts
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          '&:hover': {
            transform: 'translateY(-2px) translateZ(0)',
            boxShadow: '-10px -20px 25px 0px rgba(123, 186, 201, 0.15), 15px 30px 35px 0px rgba(123, 186, 201, 0.12)',
          },
          ...sx as object,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default GlassContainer;